import React, { useEffect, useState } from "react";
import { Button, Spinner, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import "./invoice.css";
import { useParams } from "react-router-dom";

function ParentTransactionInvoice({ parentTransactionId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const { id } = useParams();
  const customer_currency = (localStorage.getItem("customer_currency") || "").replace(/"/g, "");
  const val = (v) =>
    v !== null && v !== undefined && v !== "" ? v : "-";

  // Reversal-approval workflow state. The button no longer reverses a
  // transaction immediately — it submits a request that an admin must approve.
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [reverseReason, setReverseReason] = useState("");
  const [isSubmittingReverse, setIsSubmittingReverse] = useState(false);
  const [reverseRequested, setReverseRequested] = useState(false);

  // Picks the right label/value based on payment mode so accounting
  // reconciliation against M-Pesa or cheque records is direct.
  const getPaymentRefDisplay = (transaction) => {
    const mode = String(transaction?.transaction_mode || "").toLowerCase();
    const ref = transaction?.transaction_merchant_reference;
    if (mode.includes("mpesa") || mode.includes("m-pesa")) {
      return { label: "M-Pesa Code", value: ref };
    }
    if (mode.includes("cheque") || mode.includes("check")) {
      return { label: "Cheque Number", value: ref };
    }
    return { label: "Transaction ID", value: ref };
  };

  const handleSubmitReversalRequest = async () => {
    if (!reverseReason.trim()) {
      toast.warning("Please enter a reason for the reversal request.");
      return;
    }
    setIsSubmittingReverse(true);
    try {
      const payload = {
        customer_id: localStorage.getItem("customer_id"),
        api_key: localStorage.getItem("api_key"),
        user_id: localStorage.getItem("user_id"),
        language: "en",
        parent_transactions_id: Number(id),
        // Signals to the backend: do NOT reverse immediately. Mark the
        // transaction as Pending Reversal and notify a manager.
        request_reversal: true,
        reversal_reason: reverseReason.trim(),
      };
      const res = await simplePostCall(
        ApiConfig.parentfailedtransactionUPDATE,
        JSON.stringify(payload),
      );
      if (res?.result || res?.status) {
        toast.success(
          res?.message ||
            "Reversal requested. Awaiting manager approval before any funds are reversed.",
        );
        setReverseRequested(true);
        setShowReverseModal(false);
        setReverseReason("");
      } else {
        toast.error(res?.message || "Failed to submit reversal request.");
      }
    } catch (err) {
      console.error("Reversal request error:", err);
      toast.error("An error occurred while submitting the request.");
    } finally {
      setIsSubmittingReverse(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, []);

  const fetchInvoice = async () => {
    setLoading(true);
     const customer_id = localStorage.getItem("customer_id");
      const api_key = localStorage.getItem("api_key");
    try {
      const payload = {
        customer_id: customer_id  ,
        api_key: api_key,
        language: "en",
        parent_transactions_id: Number(id),
        action: "success",
      };

      const res = await simplePostCall(
        ApiConfig.parentfailedtransactionView,
        JSON.stringify(payload)
      );

      if (res?.result && res?.data) {
        setData(res.data);
      }
    } catch (err) {
      console.error("Invoice fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center">No invoice data found</div>;
  }

  const student = data.studentModel?.[0] || {};
  const transaction = data.parent_transaction_model?.[0] || {};
  const invoice = data.parent_invoice?.[0] || {};
  const layout = data.invoice_layout?.[0] || {};

  const paymentRef = getPaymentRefDisplay(transaction);
  const isPendingReversal =
    reverseRequested ||
    transaction.transaction_pending_reversal === 1 ||
    transaction.transaction_pending_reversal === true;

  return (
    <div className="container-fluid py-4">
      <ToastContainer />
      {/* HEADER */}
      <div className="bg-white p-3 rounded shadow-sm mb-3 d-flex justify-content-between align-items-center">
        <h5 className="fw-bold mb-0">View Receipt</h5>
        <div className="d-flex gap-2 align-items-center">
          <Button size="sm" variant="secondary" onClick={() => window.print()}>
            Print
          </Button>
          {isPendingReversal ? (
            <span className="pi-pending-deletion-badge" title="Awaiting admin approval">
              Pending Reversal
            </span>
          ) : (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setShowReverseModal(true)}
              title="Request reversal of this transaction (requires admin approval)"
            >
              Reverse Transaction
            </Button>
          )}
        </div>
      </div>

      {/* RECEIPT */}
      <div className="bg-white p-4 rounded shadow-sm">

        {/* CUSTOM HEADER FROM API */}
        {layout.receipt_layout_header && (
          <div
            className="text-center mb-3"
            dangerouslySetInnerHTML={{
              __html: layout.receipt_layout_header,
            }}
          />
        )}

        <div className="text-center mb-4">
          <div className="fw-bold">
            Sharjah - United Arab Emirates, {val(student.student_mobile)}
          </div>
          <div>{val(student.student_email)}</div>
          <h4 className="mt-3">Receipt</h4>
        </div>

        {/* STUDENT + TRANSACTION INFO */}
        <div className="border p-3 mb-4">
          <div className="row mb-2">
            <div className="col-md-4">
              <b>Admission No:</b> {val(student.student_admission_num)}
            </div>
            <div className="col-md-4">
              <b>Academic Year:</b> -
            </div>
            <div className="col-md-4">
              <b>Invoice Date:</b>{" "}
              {transaction.transaction_initialized_datetime
                ? new Date(
                    transaction.transaction_initialized_datetime
                  ).toLocaleDateString()
                : "-"}
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-md-4">
              <b>Name:</b> {val(student.student_name)}
            </div>
            <div className="col-md-4">
              <b>Receipt No:</b>{" "}
              {val(invoice.parent_transaction_invoices_id)}
            </div>
            <div className="col-md-4">
              <b>Invoice No:</b>{" "}
              {val(invoice.parent_transaction_invoices_invoice_id)}
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-md-4">
              <b>Grade:</b>{" "}
              {val(student.class_name)} ({val(student.class_division_name)})
            </div>
            <div className="col-md-4">
              <b>{paymentRef.label}:</b>{" "}
              {val(paymentRef.value)}
            </div>
            <div className="col-md-4">
              <b>Paid Date:</b>{" "}
              {transaction.transaction_updated_datetime
                ? new Date(
                    transaction.transaction_updated_datetime
                  ).toLocaleString()
                : "-"}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <b>Mode of Transaction:</b>{" "}
              {val(transaction.transaction_mode)}
            </div>
          </div>
        </div>

        {/* INVOICE TABLE (amounts not provided yet) */}
        {/* <div className="invoice-table-wrapper">
          <table className="invoice-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Particulars</th>
                <th>Terms/Semester</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>{val(invoice.parent_transaction_invoices_invoice_of)}</td>
                <td>-</td>
                <td className="summary-amount">
                  {val(invoice.parent_transaction_invoices_transaction_amount)}
                </td>
              </tr>

              <tr>
                <td></td><td></td>
                <td className="summary-label">Payment Received</td>
                <td className="summary-amount">
                  {val(transaction.transaction_amount)}
                </td>
              </tr>

              <tr>
                <td></td><td></td>
                <td className="summary-label">Balance</td>
                <td className="summary-amount">-</td>
              </tr>
            </tbody>
          </table>
        </div> */}

        <div className="invoice-table-wrapper">
  <table className="invoice-table">
    <thead>
      <tr>
        <th>#</th>
        <th>Particulars</th>
        <th>Terms/Semester</th>
        <th>Amount</th>
      </tr>
    </thead>

    <tbody>
      {/* MAIN LINE ITEM */}
      <tr>
        <td>1</td>
        <td>
          <div className="particular-main">
            {val(invoice?.parent_transaction_invoices_invoice_of)}
          </div>
          <div className="particular-sub">Arrear Discount</div>
          <div className="particular-sub">Additional Charges</div>
        </td>
        <td>-</td>
        <td className="amount-cell">
          <div>
            {val(
              invoice?.parent_transaction_invoices_transaction_amount
            )}
          </div>
          <div>-</div>
          <div>-</div>
        </td>
      </tr>

      {/* SUMMARY BLOCK */}
      <tr>
        <td></td>
        <td></td>
        <td className="summary-label">Sub Total</td>
        <td className="summary-amount">-</td>
      </tr>

      <tr>
        <td></td>
        <td></td>
        <td className="summary-label">Discount</td>
        <td className="summary-amount">-</td>
      </tr>

      <tr>
        <td></td>
        <td></td>
        <td className="summary-label">Tax/Additional Charges</td>
        <td className="summary-amount">-</td>
      </tr>

      <tr>
        <td></td>
        <td></td>
        <td className="summary-label summary-bold">
          Grand Total
        </td>
        <td className="summary-amount summary-bold">-</td>
      </tr>

      {/* PAYMENT SECTION */}
      <tr>
        <td></td>
        <td></td>
        <td className="summary-label">Payment received</td>
        <td className="summary-amount">
          {val(transaction?.transaction_amount)}
        </td>
      </tr>

      <tr>
        <td></td>
        <td></td>
        <td className="summary-label">Total Amount Paid</td>
        <td className="summary-amount">
          {val(transaction?.transaction_amount)}
        </td>
      </tr>

      <tr>
        <td></td>
        <td></td>
        <td className="summary-label">Balance</td>
        <td className="summary-amount">-</td>
      </tr>
    </tbody>
  </table>
</div>


        {/* FOOTER FROM API */}
        {/* {layout.receipt_layout_footer && (
          <div
            className="mt-4"
            dangerouslySetInnerHTML={{
              __html: layout.receipt_layout_footer,
            }}
          />
        )} */}

        {/* BELOW TABLE FOOTER TEXT */}
<div className="invoice-footer-text mt-4">

  <p>
    <strong>Payment received :</strong>{" "}
    {transaction?.transaction_amount
      ? `${transaction.transaction_amount} ${customer_currency} Only.`
      : "-"}
  </p>

  <p>
    <strong>Total Due :</strong>{" "}
    -
  </p>

    {layout.receipt_layout_footer && (
          <div
            className="mt-4"
            dangerouslySetInnerHTML={{
              __html: layout.receipt_layout_footer,
            }}
          />
        )}

</div>
      </div>

      {/* Reversal-approval modal — submits a request, does not reverse funds */}
      <Modal
        show={showReverseModal}
        onHide={() => !isSubmittingReverse && setShowReverseModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Request Transaction Reversal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            For accounting safety, payments cannot be reversed instantly.
          </p>
          <p className="mb-3">
            Submitting this request will mark the transaction as{" "}
            <b>Pending Reversal</b> and notify a manager. Funds will only be
            deducted from collected totals once an admin approves.
          </p>
          <label className="form-label fw-bold">
            Reason for reversal <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Explain why this transaction should be reversed (e.g. duplicate payment, wrong student credited)"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            disabled={isSubmittingReverse}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReverseModal(false)}
            disabled={isSubmittingReverse}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmitReversalRequest}
            disabled={isSubmittingReverse || !reverseReason.trim()}
          >
            {isSubmittingReverse ? <Spinner size="sm" /> : "Submit Reversal Request"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ParentTransactionInvoice;
